import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { functionsService } from "@/services/functionsService";

const renderPreview = (text: string) => {
  const lines = text.split("\n");
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return lines.map((line, lineIndex) => {
    const segments: React.ReactNode[] = [];
    let cursor = 0;
    let match: RegExpExecArray | null;

    while ((match = markdownLinkRegex.exec(line)) !== null) {
      const [full, label, href] = match;
      const start = match.index;
      if (start > cursor) segments.push(line.slice(cursor, start));
      segments.push(
        <a
          key={`md-${lineIndex}-${start}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blazerex-green underline"
        >
          {label}
        </a>
      );
      cursor = start + full.length;
    }

    const remaining = line.slice(cursor);
    remaining.split(urlRegex).forEach((part, idx) => {
      if (/^https?:\/\//.test(part)) {
        segments.push(
          <a
            key={`url-${lineIndex}-${idx}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blazerex-green underline"
          >
            {part}
          </a>
        );
      } else if (part) {
        segments.push(part);
      }
    });

    return (
      <p key={`line-${lineIndex}`} className="body-text text-gray-700 whitespace-pre-wrap">
        {segments.length > 0 ? segments : line}
      </p>
    );
  });
};

export const AboutManagement: React.FC = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await functionsService.getAboutContent();
        if (result?.success) {
          setContent(result.content || "");
        }
      } catch (e: any) {
        setError(e.message || "Failed to load About page content");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const preview = useMemo(() => renderPreview(content), [content]);

  const onSave = async () => {
    if (!content.trim()) {
      setError("About page text cannot be empty.");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setMessage(null);
      const result = await functionsService.updateAboutContent(content);
      if (result.success) {
        setMessage("About page updated successfully.");
      }
    } catch (e: any) {
      setError(e.message || "Failed to update About page.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="sub-header text-lg mb-2">About Page Content</h3>
        <p className="body-text text-sm text-gray-500">
          You can write plain text, paste URLs, or use markdown links like
          {" "}
          <code>[Our site](https://example.com)</code>.
        </p>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {message}
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blazerex-green"></div>
          </div>
        ) : (
          <>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={14}
              className="w-full body-text border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blazerex-green"
              placeholder="Write About page content here..."
            />
            <div className="mt-3 flex justify-end">
              <Button variant="primary" onClick={onSave} disabled={saving}>
                {saving ? "Saving..." : "Save About Page"}
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card>
        <h4 className="sub-header text-base mb-3">Preview</h4>
        <div className="space-y-3">{preview}</div>
      </Card>
    </div>
  );
};
